import { frontendURL } from 'dashboard/helper/URLHelper';

const StudioDashboard = () => import('./pages/StudioDashboard.vue');
const StudioOpenAI = () => import('./pages/StudioOpenAI.vue');
const StudioAnalytics = () => import('./pages/StudioAnalytics.vue');
const StudioAnalyticsOverview = () =>
  import('./pages/StudioAnalyticsOverview.vue');
const StudioAnalyticsCharts = () => import('./pages/StudioAnalyticsCharts.vue');
const StudioAnalyticsSatisfaction = () =>
  import('./pages/StudioAnalyticsSatisfaction.vue');
const StudioAnalyticsDetail = () => import('./pages/StudioAnalyticsDetail.vue');
const StudioAgentEdit = () => import('./pages/StudioAgentEdit.vue');
const StudioFlowBuilder = () => import('./pages/StudioFlowBuilder.vue');
const StudioPlayground = () => import('./pages/StudioPlayground.vue');
const StudioChamados = () => import('./pages/StudioChamados.vue');
const StudioProjects = () => import('./pages/StudioProjects.vue');
const StudioProjectDetail = () => import('./pages/StudioProjectDetail.vue');
const StudioTemplates = () => import('./pages/StudioTemplates.vue');
const StudioTemplateEdit = () => import('./pages/StudioTemplateEdit.vue');
const StudioMassiva = () => import('./pages/StudioMassiva.vue');
const StudioMassivaMaintenance = () =>
  import('./pages/StudioMassivaMaintenance.vue');
const StudioMassivaNotices = () => import('./pages/StudioMassivaNotices.vue');
const StudioDataTables = () => import('./pages/StudioDataTables.vue');
const StudioExternalTools = () => import('./pages/StudioExternalTools.vue');
const StudioDataTableDetail = () => import('./pages/StudioDataTableDetail.vue');
const StudioCommercialSettings = () =>
  import('./pages/StudioCommercialSettings.vue');
const StudioCommercialSales = () => import('./pages/StudioCommercialSales.vue');

const meta = {
  permissions: ['administrator'],
};

const chamadosMeta = {
  permissions: ['administrator', 'chamados_view'],
};

const massivaMeta = {
  permissions: ['administrator', 'massiva_access'],
};

const studioRoutes = {
  routes: [
    {
      path: frontendURL('accounts/:accountId/studio'),
      redirect: to => ({
        name: 'studio_settings',
        params: { accountId: to.params.accountId },
      }),
    },
    {
      path: frontendURL('accounts/:accountId/studio/settings'),
      name: 'studio_settings',
      meta,
      component: StudioOpenAI,
    },
    {
      path: frontendURL('accounts/:accountId/studio/dashboard'),
      name: 'studio_dashboard',
      meta,
      component: StudioDashboard,
    },
    {
      path: frontendURL('accounts/:accountId/studio/analytics'),
      name: 'studio_analytics',
      meta,
      component: StudioAnalytics,
      redirect: to => ({
        name: 'studio_analytics_overview',
        params: { accountId: to.params.accountId },
      }),
      children: [
        {
          path: 'overview',
          name: 'studio_analytics_overview',
          meta,
          component: StudioAnalyticsOverview,
        },
        {
          path: 'charts',
          name: 'studio_analytics_charts',
          meta,
          component: StudioAnalyticsCharts,
        },
        {
          path: 'satisfaction',
          name: 'studio_analytics_satisfaction',
          meta,
          component: StudioAnalyticsSatisfaction,
        },
        // A rota api-keys morreu: as chaves já aparecem nas Configurações do
        // Studio (StudioDashboard renderiza o mesmo ApiKeysSection).
      ],
    },
    {
      path: frontendURL(
        'accounts/:accountId/studio/analytics/sessions/:sessionId'
      ),
      name: 'studio_analytics_detail',
      meta,
      component: StudioAnalyticsDetail,
      props: route => ({
        sessionId: route.params.sessionId,
        accountId: route.params.accountId,
      }),
    },
    {
      path: frontendURL('accounts/:accountId/studio/projects'),
      name: 'studio_projects',
      meta,
      component: StudioProjects,
    },
    {
      path: frontendURL('accounts/:accountId/studio/projects/:projectId'),
      name: 'studio_project_detail',
      meta,
      component: StudioProjectDetail,
      props: route => ({ projectId: Number(route.params.projectId) }),
    },
    // Dashboard de massiva (queda de rede por caixa FTTH, compute ao vivo).
    {
      path: frontendURL('accounts/:accountId/studio/massiva'),
      name: 'studio_massiva',
      meta: massivaMeta,
      component: StudioMassiva,
    },
    // Massiva PREVISTA: avisar antes de mexer na rede. `?conexao=` vem do mapa
    // da rede (o circuito já escolhido); sem ela, o operador digita.
    {
      path: frontendURL('accounts/:accountId/studio/massiva/manutencao'),
      name: 'studio_massiva_maintenance',
      meta: massivaMeta,
      component: StudioMassivaMaintenance,
    },
    // Comunicados de rede (incidente REATIVO): a voz do operador que a IA usa
    // com os afetados. Página própria — o comunicado sobrevive à normalização
    // da rede (o grafo some do painel; ele não).
    {
      path: frontendURL('accounts/:accountId/studio/massiva/comunicados'),
      name: 'studio_massiva_notices',
      meta: massivaMeta,
      component: StudioMassivaNotices,
    },
    // Tabelas de preço da IA comercial: o usuário monta o catálogo (campos +
    // linhas) e cada tabela publicada vira tool do agente. Lista + builder.
    {
      path: frontendURL('accounts/:accountId/studio/tables'),
      name: 'studio_data_tables',
      meta,
      component: StudioDataTables,
    },
    {
      path: frontendURL('accounts/:accountId/studio/tables/:tableId'),
      name: 'studio_data_table_detail',
      meta,
      component: StudioDataTableDetail,
      props: route => ({ tableId: Number(route.params.tableId) }),
    },
    // Comercial: o que a IA precisa saber pra VENDER no ERP do provedor
    // (filial/vendedor/vencimentos) e o que ela já vendeu. Irmã de massiva e
    // tabelas — não é aba de outra página; configurar e acompanhar são
    // assuntos de naturezas diferentes.
    {
      path: frontendURL('accounts/:accountId/studio/comercial'),
      name: 'studio_commercial',
      meta,
      redirect: to => ({
        name: 'studio_commercial_settings',
        params: { accountId: to.params.accountId },
      }),
    },
    {
      path: frontendURL('accounts/:accountId/studio/comercial/configuracao'),
      name: 'studio_commercial_settings',
      meta,
      component: StudioCommercialSettings,
    },
    {
      path: frontendURL('accounts/:accountId/studio/comercial/vendas'),
      name: 'studio_commercial_sales',
      meta,
      component: StudioCommercialSales,
    },
    // Registro de ferramenta externa da conta: a definição mora aqui (uma vez),
    // e o agente vincula por referência. É onde ficam as declarações de
    // idempotência/uso — ver .ai/tool-externa-idempotencia-arquitetura-plan.md.
    {
      path: frontendURL('accounts/:accountId/studio/ferramentas'),
      name: 'studio_external_tools',
      meta,
      component: StudioExternalTools,
    },
    // Biblioteca de templates de mensagem (formatos determinísticos
    // reusáveis por N agentes/fluxos): lista + editor.
    {
      path: frontendURL('accounts/:accountId/studio/templates'),
      name: 'studio_templates',
      meta,
      component: StudioTemplates,
    },
    {
      path: frontendURL('accounts/:accountId/studio/templates/:templateId'),
      name: 'studio_template_edit',
      meta,
      component: StudioTemplateEdit,
      props: route => ({ templateId: route.params.templateId }),
    },
    // Playground escopado ao projeto — testa o fluxo principal da versao
    // escolhida. Aberto via "Testar fluxo" no detalhe do projeto.
    {
      path: frontendURL(
        'accounts/:accountId/studio/projects/:projectId/playground'
      ),
      name: 'studio_project_playground',
      meta,
      component: StudioPlayground,
      props: route => ({
        projectId: Number(route.params.projectId),
        versionId: route.query.versionId ? Number(route.query.versionId) : null,
        fromFlowId: route.query.fromFlowId
          ? Number(route.query.fromFlowId)
          : null,
      }),
    },
    // Agentes e fluxos vivem dentro do projeto — nao ha mais listas
    // standalone. /studio/agents redireciona pra lista de projetos pra
    // nao deixar a URL antiga 404.
    {
      path: frontendURL('accounts/:accountId/studio/agents'),
      redirect: to => ({
        name: 'studio_projects',
        params: { accountId: to.params.accountId },
      }),
    },
    {
      path: frontendURL('accounts/:accountId/studio/agents/:agentId'),
      name: 'studio_agent_edit',
      meta,
      component: StudioAgentEdit,
      props: route => ({ agentId: route.params.agentId }),
    },
    // /studio/flows tambem cai na lista de projetos (sem hub/lista propria).
    {
      path: frontendURL('accounts/:accountId/studio/flows'),
      redirect: to => ({
        name: 'studio_projects',
        params: { accountId: to.params.accountId },
      }),
    },
    // Builder do flow (voice e text). Abertos a partir do projeto.
    {
      path: frontendURL('accounts/:accountId/studio/flows/voice/new'),
      name: 'studio_flow_builder_new',
      meta,
      component: StudioFlowBuilder,
    },
    {
      path: frontendURL('accounts/:accountId/studio/flows/voice/:flowId'),
      name: 'studio_flow_builder_edit',
      meta,
      component: StudioFlowBuilder,
      props: route => ({ flowId: route.params.flowId }),
    },
    {
      path: frontendURL('accounts/:accountId/studio/flows/text/new'),
      name: 'text_ai_flow_builder_new',
      meta,
      component: StudioFlowBuilder,
    },
    {
      path: frontendURL('accounts/:accountId/studio/flows/text/:flowId'),
      name: 'text_ai_flow_builder_edit',
      meta,
      component: StudioFlowBuilder,
      props: route => ({ flowId: route.params.flowId }),
    },
    // Redirects das URLs antigas — mantêm bookmarks/links externos vivos.
    {
      path: frontendURL('accounts/:accountId/text-ai/flows'),
      redirect: to => ({
        name: 'studio_projects',
        params: { accountId: to.params.accountId },
      }),
    },
    {
      path: frontendURL('accounts/:accountId/text-ai/flows/new'),
      redirect: to => ({
        name: 'text_ai_flow_builder_new',
        params: { accountId: to.params.accountId },
        query: to.query,
      }),
    },
    {
      path: frontendURL('accounts/:accountId/text-ai/flows/:flowId'),
      redirect: to => ({
        name: 'text_ai_flow_builder_edit',
        params: {
          accountId: to.params.accountId,
          flowId: to.params.flowId,
        },
      }),
    },
    // Antigas /text/inbound/* → /text/* (sem o nivel intermediario)
    {
      path: frontendURL('accounts/:accountId/studio/flows/text/inbound'),
      redirect: to => ({
        name: 'studio_projects',
        params: { accountId: to.params.accountId },
      }),
    },
    {
      path: frontendURL('accounts/:accountId/studio/flows/text/inbound/new'),
      redirect: to => ({
        name: 'text_ai_flow_builder_new',
        params: { accountId: to.params.accountId },
      }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/studio/flows/text/inbound/:flowId'
      ),
      redirect: to => ({
        name: 'text_ai_flow_builder_edit',
        params: {
          accountId: to.params.accountId,
          flowId: to.params.flowId,
        },
      }),
    },
    // Playground agora vive dentro do projeto — a URL antiga sem projeto cai na
    // lista de projetos (o usuario escolhe qual projeto testar).
    {
      path: frontendURL('accounts/:accountId/studio/playground'),
      redirect: to => ({
        name: 'studio_projects',
        params: { accountId: to.params.accountId },
      }),
    },
    // Chamados NÃO fazem parte do Studio (item próprio da sidebar) — a rota
    // vive em /chamados. Os caminhos antigos /studio/chamados redirecionam.
    {
      path: frontendURL('accounts/:accountId/chamados'),
      name: 'chamados',
      meta: chamadosMeta,
      component: StudioChamados,
    },
    {
      path: frontendURL('accounts/:accountId/chamados/:ticketId'),
      name: 'chamados_detail',
      meta: chamadosMeta,
      component: StudioChamados,
      props: route => ({ ticketId: route.params.ticketId }),
    },
    {
      path: frontendURL('accounts/:accountId/studio/chamados'),
      redirect: to => ({
        name: 'chamados',
        params: { accountId: to.params.accountId },
      }),
    },
    {
      path: frontendURL('accounts/:accountId/studio/chamados/:ticketId'),
      redirect: to => ({
        name: 'chamados_detail',
        params: {
          accountId: to.params.accountId,
          ticketId: to.params.ticketId,
        },
      }),
    },
    {
      path: frontendURL('accounts/:accountId/studio/tickets'),
      redirect: to => ({
        name: 'chamados',
        params: { accountId: to.params.accountId },
      }),
    },
    {
      path: frontendURL('accounts/:accountId/studio/tickets/:ticketId'),
      redirect: to => ({
        name: 'chamados_detail',
        params: {
          accountId: to.params.accountId,
          ticketId: to.params.ticketId,
        },
      }),
    },
  ],
};

export default studioRoutes;
